/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

/**
 * Supabase SSR Browser Client for managing authentication tokens.
 * This module provides a centralized way to access the Supabase session
 * and extract the access_token for use in API requests.
 *
 * Uses @supabase/ssr's createBrowserClient for proper SSR support and
 * cookie management in the browser.
 */

import { createBrowserClient } from "@supabase/ssr";

import type { SupabaseClient } from "@supabase/supabase-js";

let supabaseClient: SupabaseClient | null = null;

/**
 * Initialize the Supabase browser client with configuration from window.gristConfig.
 * Uses createBrowserClient from @supabase/ssr for proper SSR support.
 *
 * IMPORTANT: Even if Supabase is not configured, we still register the global
 * token getter function to enable cookie-based fallback.
 */
export function initSupabaseClient(): SupabaseClient | null {
  if (typeof window === "undefined") {
    return null;
  }

  const supabaseUrl = (window as any).gristConfig?.SUPABASE_URL ||
    (window as any).GRIST_CONFIG?.SUPABASE_URL;
  const supabaseAnonKey = (window as any).gristConfig?.SUPABASE_ANON_KEY ||
    (window as any).GRIST_CONFIG?.SUPABASE_ANON_KEY;

  // Always register the global function, even if Supabase is not configured
  // This allows us to use cookie fallback
  (window as any).__getSupabaseAccessToken = getAccessToken;

  if (!supabaseUrl || !supabaseAnonKey) {
    console.info("Supabase not configured - using cookie-based authentication fallback");
    return null;
  }

  if (!supabaseClient) {
    // Use createBrowserClient from @supabase/ssr for proper SSR support
    // This handles cookies correctly and works with the server-side SSR client
    supabaseClient = createBrowserClient(supabaseUrl, supabaseAnonKey, {
      cookieOptions: {
        name: "kn-backend-auth-token",
        domain: ".knetai.com",
        path: "/",
        sameSite: "lax",
        secure: true,
      },
    });

    console.debug("Supabase SSR browser client initialized");
  }

  return supabaseClient;
}

/**
 * Get the current Supabase client instance
 */
export function getSupabaseClient(): SupabaseClient | null {
  return supabaseClient || initSupabaseClient();
}

/**
 * Get the current access token from the Supabase session.
 * Returns null if no session exists or Supabase is not configured.
 *
 * Fallback strategy:
 * 1. Try to get from Supabase client session
 * 2. If that fails, try to read directly from chunked cookies
 */
export async function getAccessToken(): Promise<string | null> {
  const client = getSupabaseClient();

  // Try to get token from Supabase client
  if (client) {
    try {
      const { data: { session }, error } = await client.auth.getSession();

      if (error) {
        console.warn("Error getting Supabase session:", error.message);
      }
      else if (session?.access_token) {
        // Check if token is expired or about to expire (within 60 seconds)
        const expiresAt = session.expires_at || 0;
        const now = Math.floor(Date.now() / 1000);
        const timeLeft = expiresAt - now;

        if (timeLeft < 60) {
          console.debug("Token expiring soon, attempting refresh...");
          const { data: { session: refreshedSession }, error: refreshError } =
            await client.auth.refreshSession();

          if (refreshError) {
            console.warn("Failed to refresh token:", refreshError.message);
            return session.access_token; // Return existing token anyway
          }

          if (refreshedSession?.access_token) {
            console.debug("Token refreshed successfully");
            return refreshedSession.access_token;
          }
        }

        console.debug("Got access token from Supabase client session");
        return session.access_token;
      }
    }
    catch (err) {
      console.warn("Error getting token from Supabase client:", err);
    }
  }

  // Fallback: Try to read token directly from chunked cookies
  try {
    const token = getTokenFromChunkedCookies();
    if (token) {
      console.debug("Got access token from chunked cookies (fallback)");
      return token;
    }
  }
  catch (err) {
    console.warn("Error reading token from cookies:", err);
  }

  console.debug("No access token available");
  return null;
}

/**
 * Fallback method to extract access_token from chunked cookies.
 * Reads kn-backend-auth-token.* cookies and combines them.
 */
function getTokenFromChunkedCookies(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  try {
    const cookies = document.cookie.split(";").reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split("=");
      if (key) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, string>);

    const cookiePrefix = "kn-backend-auth-token";

    // Check for unchunked cookie first
    if (cookies[cookiePrefix]) {
      let raw = cookies[cookiePrefix];
      // Handle base64- prefix from @supabase/ssr
      if (raw.startsWith("base64-")) {
        try {
          const b64 = raw.substring(7).replace(/-/g, "+").replace(/_/g, "/");
          const padded = b64 + "=".repeat((4 - b64.length % 4) % 4);
          raw = atob(padded);
        }
        catch (e) { /* fall through */ }
      }
      try {
        const decoded = decodeURIComponent(raw);
        const sessionData = JSON.parse(decoded);
        if (sessionData?.access_token) {
          return sessionData.access_token;
        }
      }
      catch (e1) {
        try {
          const sessionData = JSON.parse(raw);
          if (sessionData?.access_token) {
            return sessionData.access_token;
          }
        }
        catch (e2) { /* fall through to chunked */ }
      }
    }

    // Find all chunked cookie parts
    const chunks: { index: number; value: string }[] = [];

    for (const [key, value] of Object.entries(cookies)) {
      if (key.startsWith(cookiePrefix)) {
        const match = key.match(/^kn-backend-auth-token\.(\d+)$/);
        if (match) {
          const index = parseInt(match[1], 10);
          chunks.push({ index, value });
        }
      }
    }

    if (chunks.length === 0) {
      return null;
    }

    // Sort by index and combine
    chunks.sort((a, b) => a.index - b.index);
    const combined = chunks.map(c => c.value).join("");

    let decoded = combined;
    try {
      decoded = decodeURIComponent(combined);
    }
    catch (e) {
      // Ignore decoding error, fallback to raw value
    }

    // Handle base64- prefix written by @supabase/ssr (default cookieEncoding: 'base64url')
    if (decoded.startsWith("base64-")) {
      try {
        const b64 = decoded.substring(7).replace(/-/g, "+").replace(/_/g, "/");
        const padded = b64 + "=".repeat((4 - b64.length % 4) % 4);
        decoded = atob(padded);
      }
      catch (e) {
        return null;
      }
    }

    // Parse JSON
    const sessionData = JSON.parse(decoded);

    if (sessionData?.access_token) {
      return sessionData.access_token;
    }

    return null;
  }
  catch (err) {
    console.error("Error parsing chunked cookies:", err);
    return null;
  }
}

/**
 * Check if the user is authenticated with Supabase
 */
export async function isAuthenticated(): Promise<boolean> {
  const token = await getAccessToken();
  return token !== null;
}

/**
 * Sign out from Supabase
 */
export async function signOut(): Promise<void> {
  const client = getSupabaseClient();
  if (client) {
    await client.auth.signOut();
  }
}

/**
 * Manually refresh the Supabase session to pull fresh custom claims (permissions).
 * Useful after creating an org or workspace to ensure the JWT reflects new access.
 */
export async function refreshSession(): Promise<void> {
  const client = getSupabaseClient();
  if (client) {
    console.debug("Refreshing Supabase session...");
    const { error } = await client.auth.refreshSession();
    if (error) {
      console.warn("Supabase session refresh failed:", error.message);
    }
    else {
      console.debug("Supabase session refreshed successfully");
    }
  }
}
